import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { z } from 'zod';
import { useAgentStream } from '../../hooks/useAgentStream';
import { dispatchAgentAction, registerAgentActionHandlers } from '../../lib/agentActions';
import { resolveApproval, type ApprovalOutcome } from '../../lib/agentClient';
import { emitQuotaExceeded } from '../../lib/quotaEvents';
import {
  AGENT_OPEN_EVENT,
  AGENT_STATUS_ANNOUNCEMENT,
  AGENT_STATUS_EVENT,
  OPEN_AGENT_EVENT,
  type AgentStatus,
} from '../../lib/agentEvents';
// NOT re-exported (story 2-147wg): the canonical definitions now live in
// `lib/agentEvents.ts` — every consumer (`AppShell.tsx`, `TopBar.tsx`,
// `Sidebar.tsx`) imports them from there directly instead of through this
// file. Re-exporting them here would have re-triggered
// `react-refresh/only-export-components` on every one of them (the rule's
// `allowConstantExport` carve-out only recognizes a DIRECT `export const x
// = '...'` declaration, not a re-exported imported binding) — a real,
// measured regression caught while making this exact change, reverted in
// favor of updating each consumer's own import instead.
import type { AgentChatMessage, AgentEvent, AgentUIContext } from '../../types/agent';
import type { FormSchemaV2 } from '../../lib/agentShared/formSchema';
import { AgentPlanCard } from '../ui/AgentPlanCard';
import { Badge } from '../ui/Chip';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import { Cluster, Inline } from '../ui/Layout';
import { Form, FormField, FormSubmit, useZodForm } from '../ui/Form';

// Embedded "Build with AI" assistant — the frontend face of the guardrailed
// agent plane. Renders streamed activity, applies validated dom_actions,
// shows human-in-the-loop approval cards, live-updates the form builder via
// artifact events, and presents agent-suggested quick-action buttons.

// Presentation guard: models occasionally leak internal ids into prose
// despite the prompt ban (fallback models especially). Scrub anything
// id-shaped from displayed text — the ids remain in the audit log.
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const scrubIds = (text: string): string =>
  text
    // "(import_id: <uuid>)" / "(id <uuid>)" → gone entirely
    .replace(new RegExp(String.raw`\s*\((?:import_id|draft_id|id)\s*:?\s*${UUID.source}\)`, 'gi'), '')
    // "with ID <uuid>" / "the ID <uuid>" → gone entirely
    .replace(new RegExp(String.raw`\s+(?:with|the)\s+id\s+${UUID.source}`, 'gi'), '')
    // any bare uuid left over → neutral reference
    .replace(UUID, 'this document')
    // stray markdown emphasis the prompt bans but models still emit
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');

// Salvage: when the model writes next-step options as a trailing text list
// instead of calling ui_suggest_actions, lift them out and render buttons.
const extractTextActions = (text: string): { cleaned: string; actions: Array<{ label: string; prompt: string }> } => {
  const lines = text.split('\n');
  let start = lines.length;
  const items: string[] = [];
  for (let i = lines.length - 1; i >= 0; i--) {
    const raw = lines[i];
    if (raw === undefined) break;
    const line = raw.trim();
    if (!line) { if (items.length > 0) start = i; continue; }
    const m = line.match(/^(?:[*\-•]|\d+[.)])\s+(.{2,120})$/);
    const item = m?.[1];
    if (!item) break;
    items.unshift(item);
    start = i;
  }
  if (items.length < 2 || items.length > 6) return { cleaned: text, actions: [] };
  const actions = items.map((raw) => {
    const label = raw.replace(/\*\*/g, '').replace(/[.?]+$/, '').trim();
    return { label: label.length > 60 ? `${label.slice(0, 57)}…` : label, prompt: label };
  });
  return { cleaned: lines.slice(0, start).join('\n').trimEnd(), actions };
};

// Story 2-26(a)'s "$<step>.<path>" friendly-reference rendering
// (STEP_REF_RE/describeStepRef/friendlyArgValue) and its resource-noun map
// moved to `components/ui/AgentPlanCard.tsx` (story 2-144) — that card is now
// the only consumer of the plan/approval markup that needed them.

// Story 2-26(c): reword raw gateway guardrail reasons (schema/allow-list
// internals) into copy a non-technical user can act on, without discarding
// the detail — it rides along as a tooltip (⛨ lines are single-line, so a
// full collapsible would overflow the transcript).
const honestGuardrailCopy = (reason: string): { headline: string; detail: string } => {
  let headline = 'That request was blocked, so nothing happened.';
  if (/not on .* allow-list/i.test(reason)) headline = 'That action isn’t available to this assistant.';
  else if (/^invalid arguments:/i.test(reason)) headline = 'That request wasn’t valid, so nothing happened.';
  else if (/^plan step \d+/i.test(reason)) headline = 'One step of the plan couldn’t run, so the plan stopped before making that change.';
  else if (/tool call budget exceeded/i.test(reason)) headline = 'That turn asked for too many actions at once — try a smaller request.';
  return { headline, detail: reason };
};

// Story 2-26(b): matches the fallback-retry `step` text agents/src/loop.js
// emits between attempts ("Model hiccup — switching over and retrying…").
// Matched by prefix (not the exact string) so minor wording changes on the
// agents side don't silently stop being recognized here.
const RETRY_MARKER_RE = /^model hiccup/i;
const RETRY_NOTICE = 'Switching to a backup model and retrying…';

// Story 2-26(c): the single-tool approval-execution path reports a raw
// "execution failed: <handler error>" string (agents/src/gateway.js) — never
// show that verbatim; keep it as a collapsible technical detail instead.
const EXECUTION_FAILED_PREFIX = /^execution failed:\s*/i;

type ApprovalEntry = Extract<Entry, { kind: 'approval' }>;

// Story 2-26(c): a batched plan (1D-2) that stops partway through still
// resolves as `status: 'approved'` — the PLAN was approved, not every step
// ran. Previously the panel had no rendering for that at all (silently
// looked identical to full success); this surfaces the stoppage honestly,
// without dumping the failing step's raw error inline.
const honestApprovalOutcome = (entry: ApprovalEntry): { headline: string; detail?: string; isFailure: boolean } => {
  if (entry.status === 'declined') return { headline: 'Declined — nothing was changed.', isFailure: false };
  if (entry.status === 'expired') {
    return {
      headline: 'This request timed out waiting for approval — nothing was changed. Ask again if you still want it done.',
      isFailure: true,
    };
  }
  if (entry.status === 'failed') {
    const detail = entry.reason?.replace(EXECUTION_FAILED_PREFIX, '');
    return { headline: 'That action couldn’t be completed. Nothing else changed.', ...(detail ? { detail } : {}), isFailure: true };
  }
  const stopped = entry.result && entry.result.ok === false ? entry.result : null;
  if (stopped) {
    const at = (stopped.stoppedAtStep ?? 0) + 1;
    const total = entry.steps?.length ?? stopped.steps?.length ?? at;
    const failedStep = stopped.steps?.find((s) => s.status === 'failed');
    return {
      headline: total > 1
        ? `Stopped after step ${at} of ${total} — the rest were not run. Nothing already done was undone.`
        : 'That step couldn’t be completed.',
      ...(failedStep?.error ? { detail: failedStep.error } : {}),
      isFailure: true,
    };
  }
  return { headline: 'Approved.', isFailure: false };
};

// Story 2-26(d)'s `PANEL_WIDTH_PX`/`PANEL_RESERVE_PX`/`RESERVE_MIN_VIEWPORT_WIDTH`
// body-padding reserve lived here until story 2-147uc — removed outright, see
// that removal's own comment further down (the `useEffect` this fed) for why.

type Entry =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string }
  | { kind: 'activity'; tone: 'tool' | 'result' | 'dom' | 'guardrail' | 'step' | 'retry'; text: string; detail?: string | undefined }
  | {
      kind: 'approval';
      token: string;
      tool: string;
      args: Record<string, unknown>;
      steps?: Array<{ tool: string; args: Record<string, unknown> }> | undefined;
      status: 'pending' | 'approved' | 'declined' | 'expired' | 'failed';
      reason?: string | undefined;
      result?: ApprovalOutcome['result'] | undefined;
    };

interface Props {
  agentName?: string;
  context: AgentUIContext;
  onArtifact: (draftId: string, schema: FormSchemaV2) => void;
  suggestions?: string[];
  /** Opens the panel and sends this message once on mount (e.g. PDF import autopilot). */
  autoPrompt?: string | undefined;
  /** Shown as a friendly stand-in for the autoPrompt bubble (instruction text stays hidden). */
  autoPromptLabel?: string | undefined;
  /** Attachment chip pinned in the panel (e.g. the imported PDF). */
  attachment?: { name: string; detail?: string } | undefined;
  /** External work in progress (e.g. upload/transcription) — shows the ring + status line. */
  busy?: boolean;
  busyLabel?: string;
  /**
   * Raises the launcher/panel's bottom position from `bottom-6` to
   * `bottom-28` — clears a fixed bottom bar the mounting page already
   * renders below it. Default `false` = identical to every existing mount.
   * Story 2-147xd: replaced a numeric `dockOffsetPx` (px) prop + inline
   * `style={{ bottom: calc(...) }}` (design-language/no-inline-style —
   * composite is not one of the layers a computed-value style prop is
   * permitted on) with this boolean — its only real caller
   * (`TemplateEditorPage.tsx`) always passed the literal `88`, and
   * `1.5rem + 88px` is exactly `7rem`, Tailwind's own static `bottom-28`
   * spacing-scale step — so the "genuinely computed value" a class
   * supposedly couldn't express was actually a fixed design decision all
   * along, expressible statically. `88px` clears that page's own Save bar
   * (~64px pill height + bottom safe-area padding), confirmed live at
   * 1440px/390px before this conversion and unchanged by it (same computed
   * `bottom` result, `24px + 88px = 112px = 7rem`, just reached through a
   * static class instead of an inline calc).
   */
  raised?: boolean;
  /** Launcher button text. Default 'Build with AI' — override so a second, stacked
   * panel (see `raised`) reads as a distinct affordance rather than a duplicate. */
  launcherLabel?: string;
  /**
   * Story 2-133: which shell slot this instance renders into.
   * - 'floating' (default): the original self-contained launcher pill +
   *   fixed-position card, unchanged — two mount sites keep this, byte-
   *   identical behavior, for two DIFFERENT reasons (story 2-147i split this
   *   comment: the original text bundled both under one P18 §7 citation that
   *   only genuinely applies to the first):
   *     (2) PlatformConsolePage's "Owner Assistant" — `agentName=
   *   "platformOwnerAgent"`, a genuinely distinct, owner-plane-only agent
   *   identity with its own gateway allow-list, never routable for org users
   *   (P18 §7). Correctly excluded from the shell drawer: consolidating
   *   would mean the org-plane `assistant` drawer somehow also served the
   *   platform-owner copilot, conflating two identities that must stay
   *   separate by design.
   *     (3) TemplateEditorPage's panel — `agentName="assistant"`, the SAME
   *   identity the shell drawer already uses, so P18 §7 does not apply here
   *   at all. The real reason is structural, not identity-based: `App.tsx`
   *   mounts `/app/template-editor(/:presetId)` OUTSIDE
   *   `<Route element={<AppShell />}>` (its own comment: "Full-screen editor
   *   lives outside the AppShell chrome") — a deliberate, full-screen,
   *   chrome-free "SafetyCulture-style" builder with no TopBar/Sidebar
   *   (TemplateEditorPage.tsx's own header comment: "this editor renders
   *   OUTSIDE AppShell's own AppFrame instance"). Since AppShell is a layout
   *   route whose <Outlet/> only renders for routes nested under it, AppShell
   *   — and therefore its `variant="drawer"` instance — is simply never
   *   mounted on this route. There is no shell drawer present on this page to
   *   consolidate onto. (Even setting that aside: 'drawer' below has no
   *   self-contained launcher — it opens solely via TopBar's header trigger,
   *   which this chrome-free route also does not have — so reusing it here
   *   would first need a page-local open affordance and a rethink of the
   *   AGENT_STATUS_EVENT/AGENT_OPEN_EVENT single-writer guards below, which
   *   assume exactly one 'drawer' instance exists app-wide. Real, but
   *   currently unneeded machinery — filed as a backlog note, not built,
   *   since there is no shell instance on this route for it to feed today.)
   *   TemplateEditorPage's own busy/autoPrompt/attachment props (the PDF
   *   import flow) stay on THIS instance for the same reason — there is no
   *   other instance on the page to receive them.
   * - 'drawer': the shell-level instance (AppShell.tsx only). No launcher of
   *   its own — opened solely via TopBar's header trigger dispatching
   *   OPEN_AGENT_EVENT. Renders as a full-height right-edge overlay
   *   (`.shell-agent-dock`, tokens.css) driven by the `data-shell-agent`
   *   token contract (2-131) instead of `fixed bottom-6 right-6` literals,
   *   and — critically — the panel's DOM is NEVER unmounted on close: only
   *   `data-shell-agent`/CSS visibility changes, so a live stream, the
   *   composer's typed text and the transcript's scroll position all
   *   survive close/reopen and navigation.
   */
  variant?: 'floating' | 'drawer';
}

// Static display names — routing is automatic, no picker is shown.
const AGENT_TITLES: Record<string, string> = {
  assistant: 'DefectSnap Assistant',
  formBuilderAgent: 'Form Builder Agent',
  userDataAgent: 'Account Agent',
  designSystemAgent: 'Design System Agent',
  platformOwnerAgent: 'Platform Owner Assistant',
};

const DEFAULT_SUGGESTIONS = [
  'Build a full rental property inspection form with condition ratings and photo evidence',
  'Create a short Airbnb turnover checklist with smart logic for damage',
  'List my existing presets',
];

const AGENT_SUGGESTIONS: Record<string, string[]> = {
  assistant: [
    'Build a rental property inspection form with condition ratings',
    'What plan am I on and how much usage do I have left?',
    'Which inspections are still open?',
  ],
  formBuilderAgent: DEFAULT_SUGGESTIONS,
  designSystemAgent: [
    'What are our primary and accent colors, in light and dark mode?',
    'How do I build a button consistent with the design system?',
    'Which response set should a safety checklist use?',
  ],
  userDataAgent: [
    'What plan am I on and how much usage do I have left?',
    'List my properties',
    'Which inspections are still open?',
  ],
  // Story 2-27: owner-plane onboarding assistant, mounted on the Platform console.
  platformOwnerAgent: [
    'Onboard Acme Inspections on the Pro plan and invite jane@acme.com as owner',
    'List every organization and their plans',
    'Show me the full feature catalog',
  ],
};

const AGENT_PLACEHOLDERS: Record<string, string> = {
  assistant: 'Ask anything — I’ll bring in the right specialist…',
  formBuilderAgent: 'Describe the form you need…',
  designSystemAgent: 'Ask about colors, components, patterns…',
  userDataAgent: 'Ask about your account, properties, inspections…',
  platformOwnerAgent: 'Onboard or configure an organization…',
};

// Hoisted (not built inline in the component) so the shape has a stable
// identity across renders — `useMemo`'s `[]` deps below are then honest,
// not a suppressed exhaustive-deps violation.
const composerFieldShape = { message: z.string().min(1) };

export function AgentPanel({
  agentName = 'assistant', context, onArtifact,
  suggestions = DEFAULT_SUGGESTIONS, autoPrompt, autoPromptLabel, attachment,
  busy = false, busyLabel, raised = false, launcherLabel = 'Build with AI',
  variant = 'floating',
}: Props): JSX.Element {
  const dockBottomClass = raised ? 'bottom-28' : 'bottom-6';
  const [open, setOpen] = useState(false);
  const [activeAgent] = useState(agentName);
  // In Auto mode, the specialist the router picked for the current turn.
  const [routedTo, setRoutedTo] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [history, setHistory] = useState<AgentChatMessage[]>([]);
  // G-26 (story 2-95b): the composer below is a real `Form`/`useZodForm`
  // composition, not a `useState` string. No generated contract exists for
  // "send an agent message" (the agent plane is a separate service reached
  // by hand-rolled `fetch`, `lib/agentClient.ts` — not in `lib/api/*.gen.ts`
  // at all), so the schema is built from a variable before `z.object()`
  // rather than a literal — the SAME dynamic-regime idiom the runtime
  // FormSchema-v2 capture surface uses (`DesignSystemPage.tsx`'s own
  // `HideLabelComposerDemo`, built for exactly this shape).
  const composerSchema = useMemo(() => z.object(composerFieldShape), []);
  const composerForm = useZodForm(composerSchema, { defaultValues: { message: '' } });
  const [quickActions, setQuickActions] = useState<Array<{ label: string; prompt: string }>>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const assistantBufRef = useRef('');
  const contextRef = useRef(context);
  contextRef.current = context;
  // Story 2-148 part 7 finding. The `drawer` variant's closed state sets
  // `aria-hidden` but its content must also stop being reachable by
  // keyboard/pointer (see that JSX block's own comment for the full WCAG
  // 4.1.2 rationale) — `inert` is the correct DOM primitive for this, but
  // `@types/react@18` doesn't type it as a JSX attribute AND (verified live,
  // 2026-08-28: React renders the JSX `inert` prop as a no-op, confirmed via
  // `element.hasAttribute('inert')` staying `false` after setting it) React
  // 18's own attribute allowlist silently drops it even with the type
  // augmentation added for this fix (src/types/react-dom-inert.d.ts) — so it
  // is set imperatively via the real DOM property instead, which every
  // browser respects regardless of how it got set.
  const drawerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (drawerRef.current) drawerRef.current.inert = !open;
  }, [open]);

  const pushEntry = useCallback((entry: Entry) => setEntries((prev) => [...prev, entry]), []);

  const appendAssistantText = useCallback((text: string) => {
    assistantBufRef.current += text;
    setEntries((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.kind === 'assistant') {
        next[next.length - 1] = { kind: 'assistant', text: last.text + text };
      } else {
        next.push({ kind: 'assistant', text });
      }
      return next;
    });
  }, []);

  const handleEvent = useCallback((event: AgentEvent) => {
    switch (event.type) {
      case 'step': {
        // Story 2-26(b): agents/src/loop.js retries a round that dies
        // mid-stream (provider hiccup) by resetting ITS OWN buffers and
        // streaming the whole reply again from scratch — but the panel had
        // already rendered the partial assistant bubble from the failed
        // attempt's tokens. Left alone, the transcript shows that stale
        // partial bubble, then this retry line, then a SECOND (complete)
        // bubble for the same turn — the "summary rendered twice with a
        // leaked fallback line" bug. Fix: on the retry marker, drop the
        // stale partial bubble and the buffered text it came from, and
        // coalesce repeated retries into one subdued line instead of one
        // per attempt.
        if (RETRY_MARKER_RE.test(event.text)) {
          assistantBufRef.current = '';
          setEntries((prev) => {
            const trimmed = prev.length > 0 && prev[prev.length - 1]?.kind === 'assistant' ? prev.slice(0, -1) : prev;
            const last = trimmed[trimmed.length - 1];
            if (last && last.kind === 'activity' && last.tone === 'retry') return trimmed; // one line even across repeated retries
            return [...trimmed, { kind: 'activity', tone: 'retry', text: RETRY_NOTICE }];
          });
          break;
        }
        pushEntry({ kind: 'activity', tone: 'step', text: event.text });
        break;
      }
      case 'token':
        appendAssistantText(event.text);
        break;
      case 'tool_call':
        pushEntry({ kind: 'activity', tone: 'tool', text: event.label ?? `Using ${event.tool}…` });
        break;
      case 'tool_result':
        pushEntry({ kind: 'activity', tone: 'result', text: event.summary });
        break;
      case 'artifact':
        onArtifact(event.draft_id, event.schema as FormSchemaV2);
        break;
      case 'agent':
        setRoutedTo(event.displayName);
        pushEntry({ kind: 'activity', tone: 'step', text: `${event.displayName} is handling this` });
        break;
      case 'dom_action': {
        const outcome = dispatchAgentAction(event.action, event.params);
        if (!outcome.applied && event.action !== 'suggestActions') {
          pushEntry({ kind: 'activity', tone: 'guardrail', text: `UI action skipped: ${outcome.reason}` });
        }
        break;
      }
      case 'action_proposed':
        pushEntry({ kind: 'approval', token: event.approval_token, tool: event.tool, args: event.args, steps: event.steps, status: 'pending' });
        break;
      case 'guardrail': {
        const { headline, detail } = honestGuardrailCopy(event.reason);
        pushEntry({ kind: 'activity', tone: 'guardrail', text: headline, detail });
        break;
      }
      case 'quota':
        // Agent-call cap reached (story 2-17): the assistant already refused
        // conversationally in the stream; ALSO raise the 2-16 QuotaTeaser via
        // the shared quota bus (DiscoveryProvider subscribes) — same surface
        // every other capped call path gets, never a raw error bubble.
        emitQuotaExceeded({
          metric: event.metric,
          used: event.used,
          limit: event.limit,
          period: event.period,
          ...(event.upgradeUrl ? { upgradeUrl: event.upgradeUrl } : {}),
        });
        break;
      case 'done': {
        // Capture BEFORE clearing: the setHistory updater runs after this
        // handler, when the ref would already be empty.
        const finalText = assistantBufRef.current;
        assistantBufRef.current = '';
        if (finalText.trim()) {
          // History keeps the FULL text so the model remembers what it offered.
          setHistory((prev) => [...prev, { role: 'assistant', text: finalText }]);
          // If the model listed options as text instead of calling
          // ui_suggest_actions, convert them into buttons and trim the bubble.
          const { cleaned, actions } = extractTextActions(finalText);
          if (actions.length > 0) {
            setQuickActions((prev) => (prev.length > 0 ? prev : actions));
            setEntries((prev) => {
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i--) {
                const entry = next[i];
                if (entry && entry.kind === 'assistant') {
                  next[i] = { kind: 'assistant', text: cleaned || 'What would you like to do next?' };
                  break;
                }
              }
              return next;
            });
          }
        }
        break;
      }
      case 'error':
        pushEntry({ kind: 'activity', tone: 'guardrail', text: event.message });
        assistantBufRef.current = '';
        break;
    }
  }, [appendAssistantText, onArtifact, pushEntry]);

  const { send, streaming } = useAgentStream(activeAgent, handleEvent);

  const submit = useCallback((text: string, displayAs?: string) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    setQuickActions([]);
    composerForm.reset({ message: '' });
    pushEntry({ kind: 'user', text: displayAs ?? trimmed });
    const messages: AgentChatMessage[] = [...history, { role: 'user', text: trimmed }];
    setHistory(messages);
    void send(messages, contextRef.current);
  }, [composerForm, history, pushEntry, send, streaming]);

  const submitRef = useRef(submit);
  submitRef.current = submit;

  // The agent's ui_suggest_actions buttons land here.
  useEffect(() => registerAgentActionHandlers({ suggestActions: setQuickActions }), []);

  // External work (upload/transcription) opens the panel so status is visible.
  useEffect(() => {
    if (busy) setOpen(true);
  }, [busy]);

  // Story 2-129: the second way to open this panel — identical listener
  // shape to GlobalSearch's OPEN_SEARCH_EVENT (see that component), just for
  // TopBar's agent-status trigger instead of ⌘K.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_AGENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_AGENT_EVENT, onOpen);
  }, []);

  // Story 2-129: derive a coarse status from state this component already
  // tracks — no new state invented for this. 'blocked' takes priority over
  // 'working' (an unresolved approval is what's actually stalling things,
  // even if `streaming` happens to be true for a following turn); otherwise
  // 'working' while actively streaming a reply or while an external caller
  // (upload/transcription) has set `busy`. Story 2-144 adds the fourth
  // member, 'ready': once a turn has actually happened (`entries.length >
  // 0`) and nothing is streaming/blocked, there is a completed turn/result
  // sitting in the transcript for the user to review — genuinely distinct
  // from 'idle' (a fresh panel, nothing has happened yet). Still real state
  // this component already tracks, not a new flag invented for this: idle
  // and ready were previously the same bucket ('idle' covered both "never
  // used" and "quiet after a turn"); this is that bucket split honestly.
  const agentStatus: AgentStatus = entries.some((e) => e.kind === 'approval' && e.status === 'pending')
    ? 'blocked'
    : streaming || busy
      ? 'working'
      : entries.length > 0
        ? 'ready'
        : 'idle';

  // Broadcast it (see AGENT_STATUS_EVENT above) — scoped to the shell-drawer
  // instance ONLY, same guard the AGENT_OPEN_EVENT broadcast just below
  // already uses. Story 2-144 finding: before this guard, EVERY AgentPanel
  // instance dispatched on this same global `window` event unconditionally
  // (no variant check), so on /platform — where the shell drawer (AppShell,
  // `variant="drawer"`) and the Platform console's floating "Owner
  // Assistant" (`variant="floating"`, `agentName="platformOwnerAgent"`) are
  // BOTH mounted at once — an owner-assistant turn could flip the shell's
  // header pill / sidebar glow / edge glow to "working"/"blocked" even
  // though the org-wide assistant itself was untouched. TopBar/Sidebar/
  // AppShell only illuminate for the shell workspace; the two floating
  // instances (this one, and the template editor's) have no shell chrome of
  // their own to reflect into — P18 §7: contexts stay distinct. Fires
  // regardless of `open` so TopBar's pill stays truthful even if the user
  // closes the drawer while the assistant is still mid-turn or waiting on an
  // approval.
  useEffect(() => {
    if (variant !== 'drawer') return;
    window.dispatchEvent(new CustomEvent<AgentStatus>(AGENT_STATUS_EVENT, { detail: agentStatus }));
  }, [agentStatus, variant]);

  // Story 2-133: broadcast open/closed too (see AGENT_OPEN_EVENT above),
  // scoped to the shell-drawer instance only — this is the SINGLE mount
  // site TopBar's `aria-expanded` should ever reflect.
  useEffect(() => {
    if (variant !== 'drawer') return;
    window.dispatchEvent(new CustomEvent<boolean>(AGENT_OPEN_EVENT, { detail: open }));
  }, [open, variant]);

  // Story 2-26(d) originally reserved a `body`-padding gutter here for the
  // floating variant (1D-2 gate finding: the fixed bottom-right panel could
  // land over content, e.g. the Access console's members table) — nudging
  // the WHOLE PAGE's width down via `document.body.style.paddingRight` while
  // open. Story 2-133 named this exact mechanism "the measured defect" for
  // the shell-level instance — it shifted the whole page, not just the
  // panel's own footprint — and built the true, non-shifting `.shell-agent-
  // dock` overlay to fix it for that ONE instance, but explicitly left the
  // same hack running, unfixed, on the two remaining floating instances
  // (`TemplateEditorPage`, `PlatformConsolePage`) as "out of [2-133's] own
  // scope." Story 2-147uc (agent workspace composition) is the first story
  // whose own scope is exactly this component's composition end to end, and
  // closes that gap: confirmed LIVE (2026-08-29, `/platform`, 1440px) that
  // opening the Owner Assistant still reflowed the entire shell — sidebar
  // stayed put, but the TopBar's own trigger row and every column of page
  // content visibly compressed left, the identical page-wide shift 2-133
  // already named a defect worth a drawer rebuild to avoid. Removed outright
  // rather than re-scoped (e.g. to a page-local container instead of
  // `document.body`): a fixed-position overlay that occasionally sits over
  // page content, reachable by scrolling or by closing the panel, is the
  // SAME tradeoff every other overlay in this design system already makes on
  // purpose — `Modal placement="right"`'s own side sheet "opens OVER the
  // current page" with no reserved gutter either (P10 §2.5) — so this was a
  // pre-`2-133`-era special case, not a rule the rest of the tree follows.
  // Both floating instances now behave exactly like the mobile-width
  // fallback already did ("full overlay ... matches the standard mobile
  // chat-drawer pattern," this component's own prior comment): zero layout
  // shift at every width, matching the drawer variant's own guarantee,
  // without becoming the drawer.

  // Autopilot (PDF import): open the panel and fire the prompt exactly once.
  // The guard is set inside the timer callback — NOT in the effect body — so
  // React StrictMode's mount→cleanup→remount cycle can't strand it: the first
  // timer is cleared by cleanup before it fires, the second one fires.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!autoPrompt) return undefined;
    setOpen(true);
    const timer = window.setTimeout(() => {
      if (autoSentRef.current) return;
      autoSentRef.current = true;
      submitRef.current?.(autoPrompt, autoPromptLabel);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [autoPrompt, autoPromptLabel]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [entries, quickActions]);

  // Story 2-49 (duplicate approval double-click): AgentPlanCard's own
  // Approve/Decline buttons stay mounted and clickable until `status` flips
  // away from 'pending' — but that flip only happens AFTER resolveApproval's
  // fetch resolves. A fast double-click (or two near-simultaneous pointer
  // events) fires `decide()` twice before the first setEntries lands, so
  // TWO POST /v1/agents/approvals/:token requests would go out. The gateway
  // itself guarantees exactly one EXECUTION either way (gateway.js
  // resolveApproval deletes the pending entry synchronously before any
  // await, so the loser sees an 'expired'-shaped outcome, never a second
  // real run) — but the two fetches still race in the BROWSER, and if the
  // loser's response lands after the winner's, the card could flash the
  // wrong final status ("timed out" over a click that actually succeeded).
  // Guard client-side too: a Set of in-flight tokens makes the second click
  // a pure no-op instead of a second network call, so there is only ever
  // one race participant.
  const inFlightApprovalsRef = useRef<Set<string>>(new Set());
  const decide = async (token: string, approved: boolean) => {
    if (inFlightApprovalsRef.current.has(token)) return;
    inFlightApprovalsRef.current.add(token);
    let outcome: ApprovalOutcome;
    try {
      outcome = await resolveApproval(token, approved);
    } finally {
      inFlightApprovalsRef.current.delete(token);
    }
    const status = (['approved', 'expired', 'failed', 'declined'] as const).find((s) => s === outcome.status) ?? 'declined';
    setEntries((prev) => prev.map((e) =>
      e.kind === 'approval' && e.token === token
        ? { ...e, status, reason: outcome.reason, result: outcome.result }
        : e));
  };

  const toneClass: Record<string, string> = {
    tool: 'text-primary',
    result: 'text-muted-foreground',
    dom: 'text-primary',
    guardrail: 'text-amber-600 dark:text-amber-400',
    step: 'text-muted-foreground italic',
    retry: 'text-muted-foreground italic',
  };

  // Story 2-133: the shared inner tree — header, attachment chip, thread,
  // composer — is 100% unchanged from before this story (this story's own
  // scope boundary: "changes ONLY outer geometry and motion"). Hoisted out
  // of the return so both variants below render the identical subtree
  // rather than forking/duplicating it.
  const panelInner = (
    // The shell/editor drawer is a flush workspace rail; floating instances
    // retain the elevated glass card treatment used by their launcher.
    <div className={clsx(
      'flex h-full w-full flex-col overflow-hidden bg-background',
      variant === 'drawer' ? 'rounded-none border-0 shadow-none' : 'glass-card shadow-2xl',
    )}>
          {/* Story 2-144: the accessible half of the status treatment — the
              ambient glow/dot/pill (2-131/2-132/2-133) is VISUAL only, so a
              screen-reader user gets no signal from it at all. This live
              region announces the same status change in plain language,
              same `role="status" aria-live="polite" data-testid data-state`
              idiom `SaveStateIndicator.tsx` (story 2-141) already
              established for save state — reused here rather than a second
              live-region convention. Visually hidden (`sr-only`); its text
              content is the thing to assert, not its presence alone. */}
          <div
            role="status"
            aria-live="polite"
            data-testid="agent-status-announcer"
            data-state={agentStatus}
            className="sr-only"
          >
            {AGENT_STATUS_ANNOUNCEMENT[agentStatus]}
          </div>
          <header className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {AGENT_TITLES[activeAgent] ?? 'DefectSnap Assistant'}
              </p>
              <p className="text-label text-muted-foreground">
                {activeAgent === 'assistant' && routedTo
                  ? `Auto-routed to ${routedTo} · guardrailed · approvals required`
                  : 'Guardrailed · acts as you · approvals required for saves'}
              </p>
            </div>
            <IconButton
              className="text-muted-foreground"
              onClick={() => setOpen(false)}
              aria-label="Close"
              icon={<span aria-hidden="true">✕</span>}
            />
          </header>
          {attachment && (
            <Inline gap={2} className="border-b border-border/60 bg-primary/5 px-4 py-2">
              <span aria-hidden>📄</span>
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-foreground">{attachment.name}</p>
                {attachment.detail && <p className="text-caption text-muted-foreground">{attachment.detail}</p>}
              </div>
              {/* Story 2-147qb: was a hand-rolled span reproducing `Badge`'s
                  own visual signature with a pill shape `Badge` had no
                  radius option for — `radius="pill"` closes that gap. */}
              <Badge tone="primary" radius="pill" className="ml-auto">
                attached
              </Badge>
            </Inline>
          )}

          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
            {entries.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {activeAgent === 'assistant'
                    ? 'Ask anything — building forms, importing PDFs, your account, properties, or inspections. The right specialist picks it up automatically.'
                    : activeAgent === 'formBuilderAgent'
                      ? 'Describe the inspection form you need — I’ll build it live in the builder, section by section.'
                      : activeAgent === 'designSystemAgent'
                        ? 'Ask me anything about the DefectSnap design system — tokens, components, patterns.'
                        : activeAgent === 'platformOwnerAgent'
                          ? 'Onboard a new organization or configure an existing one — plans, feature flags, invites. Owner-only; every write needs your approval.'
                          : 'Ask me about your account, plan, usage, properties, or inspections.'}
                </p>
                {/* Story 2-147xd: left as a direct `Button` — a chat-composer
                    SUGGESTION CHIP (full-width, left-aligned, its own
                    border/tint chrome distinct from any DS button variant),
                    not a command cluster. No other flagged call site shares
                    this exact chrome (the quick-action pill below is a
                    genuinely different rounded-full/tinted shape, the FAB
                    launcher a third) — three one-offs in one file, not one
                    repeated shape, so growing a field for any of them would
                    be speculative rather than real convergence. */}
                {(AGENT_SUGGESTIONS[activeAgent] ?? suggestions).map((s) => (
                  <Button
                    key={s}
                    variant="ghost"
                    size="fit"
                    onClick={() => submit(s)}
                    className="block w-full justify-start rounded-xl border border-border bg-white/50 px-3 py-2 text-left text-xs font-normal text-foreground hover:bg-white/50 hover:border-primary dark:bg-white/5"
                  >
                    {s}
                  </Button>
                ))}
              </div>
            )}
            {entries.map((entry, i) => {
              if (entry.kind === 'user') {
                return (
                  <div key={i} className="ml-8 rounded-2xl rounded-br-sm bg-primary/90 px-3 py-2 text-sm text-white">
                    {entry.text}
                  </div>
                );
              }
              if (entry.kind === 'assistant') {
                return (
                  <div key={i} className="mr-6 whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-white/60 px-3 py-2 text-sm text-foreground dark:bg-white/10">
                    {scrubIds(entry.text)}
                  </div>
                );
              }
              if (entry.kind === 'approval') {
                // Story 2-144: rendering moved to `components/ui/AgentPlanCard`
                // (P10 §2.4) — this component keeps every piece of actual
                // decision logic (decide/resolveApproval, honestApprovalOutcome),
                // handing the card only plain props/callbacks.
                return (
                  <AgentPlanCard
                    key={i}
                    tool={entry.tool}
                    args={entry.args}
                    steps={entry.steps}
                    status={entry.status}
                    outcome={entry.status === 'pending' ? undefined : honestApprovalOutcome(entry)}
                    onApprove={() => decide(entry.token, true)}
                    onDecline={() => decide(entry.token, false)}
                  />
                );
              }
              return (
                <p
                  key={i}
                  className={`text-label ${toneClass[entry.tone]}`}
                  title={entry.detail}
                >
                  {entry.tone === 'guardrail' ? '⛨ ' : entry.tone === 'retry' ? '↻ ' : '· '}{scrubIds(entry.text)}
                </p>
              );
            })}
            {quickActions.length > 0 && (
              // Story 2-147xd: left as direct `Button`s — a chat-transcript
              // QUICK-ACTION PILL (rounded-full, always primary-tinted,
              // never a `pressed`/selected state), a different shape from
              // this file's own suggestion chip (block/left-aligned) and FAB
              // (fixed-position) above/below — three one-offs, not one
              // repeated shape (see the suggestion-chip comment above for
              // the full reasoning).
              <Cluster gap={2} className="pt-1">
                {quickActions.map((a) => (
                  <Button
                    key={a.label}
                    variant="ghost"
                    size="fit"
                    onClick={() => submit(a.prompt)}
                    className="rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
                  >
                    {a.label}
                  </Button>
                ))}
              </Cluster>
            )}
            {busy && (
              <p className="text-label italic text-muted-foreground">
                {busyLabel ?? 'Working…'}
              </p>
            )}
            {streaming && <p className="text-label italic text-muted-foreground">working…</p>}
          </div>

          {/* Story 2-144: rebuilt on DS primitives (Input/Button); story
              2-95b: the raw `<form>` is now the real `Form`/`useZodForm`
              composition (G-26) — same behavior as before (Enter-to-submit
              via the form's own onSubmit, disabled while streaming,
              identical placeholder copy). `hideLabel` (grown for exactly
              this row, see `Form.tsx`'s own header comment) keeps the field
              accessibly named "Message" without a second visible label line
              breaking this dense, fixed-height footer row. */}
          <Form
            form={composerForm}
            onSubmit={(values) => submit(values.message)}
            className="border-t border-border/60 p-3"
          >
            <Inline gap={2}>
              <FormField name="message" label="Message" hideLabel className="flex-1">
                {(field) => (
                  <Input
                    className="w-full"
                    placeholder={AGENT_PLACEHOLDERS[activeAgent] ?? 'Ask the agent…'}
                    {...field}
                    disabled={streaming}
                  />
                )}
              </FormField>
              <FormSubmit disabled={streaming || !composerForm.watch('message')?.trim()}>
                Send
              </FormSubmit>
            </Inline>
          </Form>
    </div>
  );

  if (variant === 'drawer') {
    return (
      // Story 2-133: the shell drawer. `.shell-agent-dock` (tokens.css) —
      // ported from docs/design/sitewise-ux-reference.html's
      // `.swa-agent-dock` treatment — is a `position: fixed` overlay pinned
      // to the right edge (fixed, not absolute, so it anchors to the real
      // viewport rather than AppShell's own min-h-screen-floored root — see
      // that class's own header comment for the exact bug this avoids),
      // running from below the header to the bottom, width
      // driven by `--shell-agent-current-w` (0px closed / --shell-agent-w
      // open, tokens.css's existing `data-shell-agent` contract, story
      // 2-131). ALWAYS mounted (no `{open && ...}` conditional — that was
      // the pre-existing bug this story fixes: the panel body used to be
      // torn out of the DOM on every close, discarding scroll position and
      // the composer's live DOM even though the entries/history/streaming
      // STATE survived in this component's own hooks above). Visibility is
      // a pure CSS/token reassignment under `data-shell-agent`, so an
      // in-flight stream keeps running and the transcript is exactly where
      // the user left it on reopen. `data-print="hide"` — same report-print
      // hook the floating variant below already relies on.
      <div
        ref={drawerRef}
        data-shell-agent={open ? 'open' : 'closed'}
        data-print="hide"
        aria-hidden={!open}
        // Story 2-148 part 7 finding, fixed here: `aria-hidden` alone hides
        // this panel from the accessibility tree but does NOT remove its
        // buttons/input from the Tab order or block clicks — a real
        // WCAG 4.1.2 violation (axe's `aria-hidden-focus`, `serious`) caught
        // live by the new standing a11y check, present on EVERY authenticated
        // route since this panel is always mounted (see this block's own
        // comment above for why: the ALWAYS-mounted design is deliberate,
        // predates this fix). The real fix (`inert`, set imperatively via
        // `drawerRef` above — see that hook's own comment for why not a JSX
        // prop) is additive: `open` still drives the real CSS/token
        // visibility (`data-shell-agent`, tokens.css); `inert` only stops the
        // CLOSED panel's off-screen content from being tabbable/clickable
        // while it's invisible.
        className={clsx('shell-agent-dock', (streaming || busy) && 'agent-working-ring')}
      >
        {panelInner}
      </div>
    );
  }

  return (
    <>
      {/* `data-print="hide"` — the report print stylesheet's real hook
          (`reportPrint.css`'s `body.report-print [data-print='hide']`
          rule). Real bug, found live (2026-08-22): this floating launcher/
          panel is mounted at the shell level (`AppShell.tsx`) and stayed
          visible in every report print/PDF-preview, since the stylesheet's
          chrome selectors never named it at all. Story 2-133: the shell-
          level mount (AppShell.tsx) now uses `variant="drawer"` above
          instead — this floating launcher/card path is only reachable from
          the two OTHER mount sites (PlatformConsolePage's "Owner Assistant",
          TemplateEditorPage's panel), unchanged.

          Story 2-147xd: left as a direct `Button` — a fixed-position FAB
          launcher (`fixed`, `rounded-full`, its own shadow/z-index), a
          genuine one-off with no other flagged call site sharing this shape
          (see the suggestion-chip/quick-action-pill comments above for the
          other two, also one-off, shapes in this same file). */}
      {!open && (
        <Button
          variant="primary"
          size="fit"
          onClick={() => setOpen(true)}
          data-print="hide"
          icon="✦"
          className={`fixed ${dockBottomClass} right-6 z-40 rounded-full px-4 py-3 text-sm font-semibold shadow-lg`}
        >
          {launcherLabel}
        </Button>
      )}
      {open && (
        <div
          data-print="hide"
          className={`fixed ${dockBottomClass} right-6 z-40 h-agent-dock max-h-agent-dock w-agent-dock max-w-[calc(100vw-2rem)] rounded-agent-dock ${
            streaming || busy ? 'agent-working-ring' : ''
          }`}
        >
          {panelInner}
        </div>
      )}
    </>
  );
}
